import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Stack,
  Button,
  Collapse,
  Alert,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ReceiptIcon from '@mui/icons-material/Receipt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import OrdersGrid from './components/OrdersGrid';
import CuttingView from './components/CuttingView';
import ManualOrderForm from './components/ManualOrderForm';
import OrderDetailDrawer from './components/OrderDetailDrawer';
import { openLetters } from './lib/letters';
import { downloadReceipts } from './lib/receipts';
import {
  listenActive,
  getOrdersByStatus,
  getOrdersPage,
  setHeaderManualStatus,
  setLineItemStatus,
  setSortKeys,
  searchByBuyerName,
  searchByUserId,
} from './services/ordersService';
import { STATUS, WORKSHOP_STATUSES, ACTIVE_LIMIT, HISTORY_PAGE_SIZE } from './constants';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'workshop', label: 'Workshop' },
  { key: 'embroidery', label: 'Embroidery' },
  { key: STATUS.UNCUT, label: 'Uncut' },
  { key: STATUS.NEW_DESIGN, label: 'New Design' },
  { key: STATUS.IN_PROGRESS, label: 'In progress' },
  { key: STATUS.CUT, label: 'Cut' },
  { key: STATUS.SEWN, label: 'Sewn' },
  { key: STATUS.ON_HOLD, label: 'On hold' },
  { key: STATUS.QUERY, label: 'Query' },
  { key: STATUS.COMPLETE, label: 'Complete' },
];

// Tabs whose data is derived (client-side) from the live active set, filtered by
// effective header status. (All of these are "active" — not done.)
const ACTIVE_DERIVED = [
  STATUS.UNCUT,
  STATUS.NEW_DESIGN,
  STATUS.IN_PROGRESS,
  STATUS.CUT,
  STATUS.SEWN,
  STATUS.ON_HOLD,
  STATUS.QUERY,
];
// Tabs that fetch their own (one-shot) data.
const FETCHED = [STATUS.COMPLETE, 'all'];

export default function OrdersPage() {
  const [activeOrders, setActiveOrders] = useState([]);
  const [activeLoading, setActiveLoading] = useState(true);
  const [activeError, setActiveError] = useState(null);

  const [tab, setTab] = useState('all');
  const [fetched, setFetched] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchCursor, setFetchCursor] = useState(null);
  const [fetchHasMore, setFetchHasMore] = useState(false);

  const [searchMode, setSearchMode] = useState('name');
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const [manualOpen, setManualOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [openOrderId, setOpenOrderId] = useState(null);
  const [fetchNonce, setFetchNonce] = useState(0);

  // Live subscription to the active set.
  useEffect(() => {
    setActiveLoading(true);
    const unsub = listenActive(
      (orders) => {
        setActiveOrders(orders);
        setActiveLoading(false);
        setActiveError(null);
      },
      (err) => {
        setActiveError(err.message || String(err));
        setActiveLoading(false);
      }
    );
    return unsub;
  }, []);

  // Load the first page for Complete / All tabs (cursor-paginated; Load more appends).
  useEffect(() => {
    if (!FETCHED.includes(tab)) {
      setFetched([]);
      setFetchCursor(null);
      setFetchHasMore(false);
      return undefined;
    }
    let cancelled = false;
    setFetchLoading(true);
    setFetched([]);
    setFetchCursor(null);
    setFetchHasMore(false);
    const run = tab === 'all' ? getOrdersPage({}) : getOrdersByStatus(tab, {});
    run
      .then((res) => {
        if (cancelled) return;
        setFetched(res.orders);
        setFetchCursor(res.lastDoc);
        setFetchHasMore(res.orders.length === HISTORY_PAGE_SIZE && !!res.lastDoc);
      })
      .finally(() => {
        if (!cancelled) setFetchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, fetchNonce]);

  async function loadMore() {
    if (!fetchCursor || fetchLoading) return;
    setFetchLoading(true);
    try {
      const res = tab === 'all' ? await getOrdersPage({ cursor: fetchCursor }) : await getOrdersByStatus(tab, { cursor: fetchCursor });
      setFetched((prev) => [...prev, ...res.orders]);
      setFetchCursor(res.lastDoc);
      setFetchHasMore(res.orders.length === HISTORY_PAGE_SIZE && !!res.lastDoc);
    } finally {
      setFetchLoading(false);
    }
  }

  const displayedOrders = useMemo(() => {
    if (searchResults !== null) return searchResults;
    if (tab === 'active') return activeOrders;
    if (tab === 'workshop') return activeOrders.filter((o) => WORKSHOP_STATUSES.includes(o.workflow?.status));
    if (tab === 'embroidery') {
      // Orders with embroidery still to do (a Pending embroidery line).
      return activeOrders.filter((o) =>
        (o.lineItems || []).some((li) => li.isEmbroidery && li.status === STATUS.PENDING)
      );
    }
    if (ACTIVE_DERIVED.includes(tab)) return activeOrders.filter((o) => o.workflow?.status === tab);
    return fetched; // Complete / All
  }, [searchResults, tab, activeOrders, fetched]);

  // Reordering applies to the whole active set, so only the (unfiltered) Active
  // tab is reorderable — reordering a filtered subset would scramble hidden rows.
  const reorderable = searchResults === null && tab === 'active';
  const loading = searchResults !== null ? searching : FETCHED.includes(tab) ? fetchLoading : activeLoading;

  // The order currently open in the detail drawer (looked up from loaded sets).
  const openOrder = useMemo(() => {
    if (!openOrderId) return null;
    for (const list of [activeOrders, fetched, searchResults || []]) {
      const found = list.find((o) => o.id === openOrderId);
      if (found) return found;
    }
    return null;
  }, [openOrderId, activeOrders, fetched, searchResults]);

  const handleSaved = useCallback(() => {
    // Active is live; refresh fetched/search views so edits show immediately.
    setFetchNonce((n) => n + 1);
    if (searchResults !== null) runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);

  const handleHeaderStatus = useCallback((order, status) => {
    setHeaderManualStatus(order, status).catch((e) => console.error('Header status update failed', e));
  }, []);

  const handleLineStatus = useCallback((order, lineId, status, opts) => {
    setLineItemStatus(order, lineId, status, opts).catch((e) => console.error('Line status update failed', e));
  }, []);

  const handleMove = useCallback(
    async (index, dir) => {
      const target = index + dir;
      if (target < 0 || target >= displayedOrders.length) return;
      // Move within an in-memory copy, then reassign clean sequential sortKeys.
      // (Plain swap fails when same-day orders share an identical sortKey.)
      const list = [...displayedOrders];
      const [moved] = list.splice(index, 1);
      list.splice(target, 0, moved);
      const STEP = 1000;
      const updates = [];
      list.forEach((o, i) => {
        const want = i * STEP;
        if ((o.workflow?.sortKey ?? -1) !== want) updates.push({ id: o.id, sortKey: want });
      });
      if (updates.length === 0) return;
      try {
        await setSortKeys(updates);
      } catch (e) {
        console.error('Reorder failed', e);
      }
    },
    [displayedOrders]
  );

  const toggleSelect = useCallback((orderId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  }, []);

  // Letters/receipts target the selected orders, or all displayed if none selected.
  const targetOrders = useMemo(() => {
    if (selectedIds.size === 0) return displayedOrders;
    return displayedOrders.filter((o) => selectedIds.has(o.id));
  }, [selectedIds, displayedOrders]);

  function handleLetters() {
    try {
      const n = openLetters(targetOrders);
      if (n === 0) window.alert('No letters generated (all targeted orders are past customers).');
    } catch (e) {
      window.alert(e.message);
    }
  }

  function handleReceipts() {
    downloadReceipts(targetOrders);
  }

  async function runSearch() {
    const q = searchText.trim();
    if (!q) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const results = searchMode === 'userId' ? await searchByUserId(q) : await searchByBuyerName(q);
      setSearchResults(results);
    } catch (e) {
      console.error('Search failed', e);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  function clearSearch() {
    setSearchText('');
    setSearchResults(null);
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {activeError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load orders: {activeError}
          {/\bindex\b/i.test(activeError) ? ' (a Firestore index may still be building — try again shortly.)' : ''}
        </Alert>
      )}

      {activeOrders.length >= ACTIVE_LIMIT && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Showing the first {ACTIVE_LIMIT} active orders only. Mark finished orders Complete to clear the
          working set, or use the status/All tabs to page through the rest.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }} alignItems="center">
        <TextField
          select
          size="small"
          label="Search by"
          value={searchMode}
          onChange={(e) => setSearchMode(e.target.value)}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="name">Buyer name</MenuItem>
          <MenuItem value="userId">Platform user ID</MenuItem>
        </TextField>
        <TextField
          size="small"
          placeholder={searchMode === 'userId' ? 'exact user id' : 'name starts with…'}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          sx={{ minWidth: 240 }}
        />
        <Button variant="outlined" size="small" startIcon={<SearchIcon />} onClick={runSearch}>
          Search
        </Button>
        {searchResults !== null && (
          <Button size="small" onClick={clearSearch}>
            Clear
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button component={RouterLink} to="/orders/import" variant="outlined" size="small" startIcon={<UploadFileIcon />}>
          Import CSV
        </Button>
        <Button variant="contained" size="small" startIcon={<AddCircleIcon />} onClick={() => setManualOpen(true)}>
          New direct order
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {selectedIds.size > 0
            ? `${selectedIds.size} of ${displayedOrders.length} orders selected`
            : `${displayedOrders.length} order${displayedOrders.length === 1 ? '' : 's'} loaded${
                searchResults === null && FETCHED.includes(tab) && fetchHasMore ? ' (more load as you page to the end)' : ''
              } — actions apply to all`}
        </Typography>
        <Button size="small" startIcon={<MailOutlineIcon />} onClick={handleLetters} disabled={targetOrders.length === 0}>
          Create letters
        </Button>
        <Button size="small" startIcon={<ReceiptIcon />} onClick={handleReceipts} disabled={targetOrders.length === 0}>
          Prepare receipts
        </Button>
        {selectedIds.size > 0 && (
          <Button size="small" onClick={() => setSelectedIds(new Set())}>
            Clear selection
          </Button>
        )}
      </Stack>

      <Collapse in={searchResults === null}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 1, minHeight: 36 }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} sx={{ minHeight: 36, py: 0 }} />
          ))}
        </Tabs>
      </Collapse>

      {searchResults !== null && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {searchResults.length} search result{searchResults.length === 1 ? '' : 's'}
        </Typography>
      )}

      {searchResults === null && tab === 'workshop' ? (
        <CuttingView orders={activeOrders} onLineStatus={handleLineStatus} onOpenOrder={setOpenOrderId} />
      ) : (
        <OrdersGrid
          orders={displayedOrders}
          loading={loading}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onHeaderStatus={handleHeaderStatus}
          onLineStatus={handleLineStatus}
          onMove={reorderable ? handleMove : undefined}
          onOpenOrder={setOpenOrderId}
          hasMore={searchResults === null && FETCHED.includes(tab) && fetchHasMore}
          onLoadMore={loadMore}
        />
      )}

      {searchResults === null && FETCHED.includes(tab) && fetchHasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <Button variant="outlined" onClick={loadMore} disabled={fetchLoading}>
            {fetchLoading ? 'Loading…' : `Load more (${fetched.length} loaded)`}
          </Button>
        </Box>
      )}

      <ManualOrderForm
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onSaved={() => setTab('active')}
      />

      <OrderDetailDrawer
        order={openOrder}
        open={!!openOrderId}
        onClose={() => setOpenOrderId(null)}
        onSaved={handleSaved}
      />
    </Box>
  );
}
